
import { Paper, Select, Space, TextInput, Text, LoadingOverlay } from '@mantine/core'
import { useForm } from '@mantine/form'
import { DateTime } from 'luxon'
import { GetServerSideProps } from 'next'
import Head from 'next/head'
import Link from 'next/link'

import { useEffect, useState } from 'react'
import Checkout, { CountryAndState } from '../../components/Checkout'

import { MenuId } from '../../components/Header'
import Layout from '../../components/Layout'
import { DSTWarning, DateAndTime, Price } from '../../components/Localization'

import { getJWTTokenFromUser, getUserInformation, getVideoFromSlug, redirectUrl } from '../../lib/data'
import { OnlineSessionShoppingCartItem, RusAuthProps, RusContextProps, SearchJson, ShoppingCartItemType, SubscriberLevel, Video } from '../../lib/definitions'

import { useRawUmberContext } from '../../lib/rawumberprovider'

import styles from './[video].module.scss'

import PhoneInput from 'react-phone-input-2'
import 'react-phone-input-2/lib/style.css'
import { getAuth } from '@clerk/nextjs/server';

//This isn't cached for logged on users
//But is cached if you're not
type Data = {
  video : Video |null,
  dateProcessed: string,
  campaign?: string
    }

export default function VideoPage({data} : {data: Data} & RusAuthProps & RusContextProps)
{
  
  const {setShoppingCartItem, shoppingCartItem, localizationInformation} = useRawUmberContext();
  const [working, setWorking] = useState(false);
  const [phoneCountryCode, setPhoneCountryCode] = useState('GB');
  const form = useForm({
    initialValues: {
      email:  '',
      email2: '',
      firstName: '',
      quantity: '',
      countryCode:'',
      stateCode:'',
      phoneNumber:''
    },

    validate: {
      email: (value) => (/^\S+@\S+$/.test(value) ? null : 'Invalid email'),
      email2: (value, values) => (value==values.email ? null : "The email addresses don't match"),
      firstName:(value)=>(value.trim().length!=0)?null:"Please give your first name",
    
    },
  });

  function setCountryCode(value: string|null)
  {
    form.values.countryCode = value?value:'';
  }

  function setStateCode(value: string|null)
  {
    form.values.stateCode = value?value:'';
  }

  function setPhoneNumber(value: string)
  {
    form.values.phoneNumber = value?value:'';
  }

  useEffect(
    () =>
    {
      if (shoppingCartItem && shoppingCartItem.itemType!=ShoppingCartItemType.none)
      {
      
      form.setValues({email: '' + shoppingCartItem?.emailAddress,
                    email2: '' + shoppingCartItem?.emailAddress,
                    quantity: '' + shoppingCartItem?.quantity,
                    firstName: '' + shoppingCartItem?.firstName,
                    countryCode: '' + shoppingCartItem?.countryCode,
                    stateCode: '' + shoppingCartItem?.stateCode,
                    phoneNumber: ''+ shoppingCartItem?.phoneNumber

                  });
      }
      else
      {

        form.setValues({quantity:'1', countryCode:localizationInformation?.CountryCode});
        
      }


    },[shoppingCartItem, localizationInformation]);

    useEffect(() =>
    {

      setPhoneCountryCode('' + localizationInformation?.CountryCode.toLowerCase());
    }, [localizationInformation]);
  
  //const { video } = router.query

  //should only get here if video is in the future AND it's not accessible
  //OR it's in the past (and may or may not be accessible)

  let description =<>{data.video?.description}</>

  if (data.video?.htmlDescription)
  {
    let html = {__html: data.video?.htmlDescription};
    description = <div dangerouslySetInnerHTML={html}></div>
  }

  function reset()
  {
    setWorking(false);
  }

  function validateMe()
  {


    setWorking(true);

    let x= form.validate().hasErrors;

    if (x)
    {
      return null;
    }


    if (form.values.countryCode == null || ('' + form.values.countryCode).length==0)
    {

      return null;
    }


    let cart: OnlineSessionShoppingCartItem = {
      emailAddress: form.values.email,
      quantity: Number(form.values.quantity),
      firstName: form.values.firstName,
      itemType: ShoppingCartItemType.onlineSession,
      countryCode: form.values.countryCode,
      stateCode:form.values.stateCode,
      optIn:true,
      phoneNumber: form.values.phoneNumber,
      
      data: 
      {
        videoId: data.video?.id??0
      }
    };

    setShoppingCartItem(cart);
    return cart;

  }

  let free = (data.video?.subscriberLevel==SubscriberLevel.none);

  let id = (data.video?data.video.id:0);

  let freeText = <>Tickets are free, but you do need to book using the form below:</>;
  let freeHeading = <>FREE!</>

  if ((data.campaign=='ta' && id==805) || (data.campaign=='ta2412' && id ==815))
    {
      //this is a special session!!
      free = true;
      freeText = <>Tickets usually cost <Price gbp={5}/> each, but are FREE to The Artist newsletter subscribers. Just register below:</>
      freeHeading = <>FREE to The Artist newsletter subscribers!</>
    }
    else if (data.campaign=='pg2412' && id ==810)
    {
      free = true;
      freeText = <>Tickets usually cost <Price gbp={5}/> each, but are FREE to Pegasus Art customers. Just register below:</>
      freeHeading = <>FREE to Pegasus Art customers!</>
    }
    

  let dateWarning = DSTWarning(DateTime.fromISO(data.video?.dateLive!));

  let seven =  <Paper shadow="md" radius="md" p="sm" withBorder style={{position:"relative", width:"100%"}}>
  <Text ta='center'>Book now and we&rsquo;ll email you a link to <b>SEVEN</b> of our previous sessions <b>and</b> a voucher worth <Price gbp={5}/> for a future session!</Text>
  </Paper>

  return(
  <Layout menuId={MenuId.online} hidePopUp>
  
  <Head>
      <title>{data.video?.title}</title>
      <meta name="description" content={data.video?.description}/>

  </Head>
  <div className = {styles.mainContent}>
  <h1 className={styles.h1}>{data.video?.title}</h1>
  <h2 className={styles.h2}>{data.video?.tutor?.name}</h2>

  {data.video && <h3 className={styles.h3}><DateAndTime dateTimeISO={data.video?.dateLive} showTime/>{dateWarning && " ** "}. {!free && <Price gbp={5}/>}{free && freeHeading}</h3>}
  <Space h='sm'/>

  {dateWarning &&<Text mt='sm' mb='sm'><b style={{color:"red"}}>** Note that if you are in the USA or Canada then this time may not be the one you are used to.</b></Text>}

  {seven}
      <Space h='sm'/>
    <div className = {styles.dangerousHTML}>{description}</div>

    <Space h="md" />

    {seven}

      <Space h="xl" />
    <Paper shadow="md" radius="md" p="sm" withBorder style={{position:"relative", maxWidth: "600px", minWidth:"200px", marginLeft:"auto", marginRight:"auto"}}>
    <LoadingOverlay visible={working} overlayProps={{ blur: 2 }} />
      <Text fz="lg" fw={700}>
        {!free && <>Tickets cost <Price gbp={5}/> each. To book, please enter your details below:</>}
        {free && freeText}        
        </Text>
      <Space h="md" />
    <form onSubmit={form.onSubmit((values) => {})}>

    <TextInput label="First name" required
    {...form.getInputProps('firstName')}
    />
      
        <TextInput required
      type="email"
            label="Email"
          placeholder="your@email.com"
         
          {...form.getInputProps('email')}
        />

<TextInput
      type="email" required
            label="Re-enter your email"
          placeholder="your@email.com"
          {...form.getInputProps('email2')}
        />

{!free && <CountryAndState defaultCountryCode={'' + localizationInformation?.CountryCode} setCountryCode={setCountryCode} setStateCode={setStateCode}/>}

<Select
      label="Number of tickets"
      placeholder="Pick one"
      {...form.getInputProps('quantity')}
      data={[
        { value: '1', label: '1' },
        { value: '2', label: '2' },
        { value: '3', label: '3' },
        { value: '4', label: '4' },
      ]}
    />

<Text size='sm' fw={500}>Optional! Want an SMS reminder 15 minutes before the session starts?</Text>

<PhoneInput
  country={phoneCountryCode}
  value={form.values.phoneNumber}
  onChange={phone => setPhoneNumber(phone)}
  placeholder='This is optional!'
  preferredCountries={['gb','us','ca','au','de','fr','ie','nl','es']}

/>


<Space h="md" />

<Checkout validationCallback={validateMe} resetCallback={reset} free={free} allowVoucher={true}/>

        <Space h="md" />
        <Text size="xs" c='#555'>By registering, you agree to our <Link href='/privacy-policy'>privacy policy</Link>. In brief, we will email you to let you know about upcoming sessions. You can unsubscribe at any time and we will never share your data.</Text> 


        </form>
     
 </Paper>
  </div>
  <Space h="lg" />

  <div className={styles.booking}>

  </div>

</Layout>
)


}

export const getServerSideProps : GetServerSideProps = async (context)=>
{
  var u2 = await redirectUrl(context.resolvedUrl);

    if (u2)
    {
      return {
        redirect: 
        {
          destination: "/" + u2,
          permanent: false
        }
      }
    }
    
    let user = await getUserInformation(context.req);
    let dt = DateTime.now().toUTC().toISO();
    let {n,utm_campaign} = context?.query;

    let allowOld = !((typeof n === 'undefined') || n==='undefined');

    let token;
    
    if (user.subscribed)
    {
        token = await getJWTTokenFromUser(user);
    }

    const slug = context.params?.video as string;

    //const { getToken } = await 


    const {getToken} = await getAuth(context.req);
    const clerkToken = await getToken();

    let vid = await getVideoFromSlug(slug, token, clerkToken);


    if (!vid)
    {
      return {
        notFound: true
      }
    }


    if ((!vid.inTheFuture && !allowOld) || vid.isSeries || !(vid.bookingUrl))
    {
        return {
            redirect:
            {
              destination: `/videos/${vid.slug}`,
              permanent:false
            }
        }
    
    }

    if (utm_campaign)
    {
      //need to get this from the database!!
      //DON'T FORGET TO UPDATE NGINX
    }

  return {
    props: {
      data: 
      { 
        video:vid,
        dateProcessed: dt,
        campaign: '' + utm_campaign
      }
    },
  }


}